import { useState, useEffect, useMemo, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type ColumnSizingState,
  type FilterFn,
} from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { DataTableProps } from "./DataTable/types";
import { ColumnConfigDialog } from "./DataTable/ColumnConfigDialog";
import { FilterDialog } from "./DataTable/FilterDialog";
import { CellWithTooltip } from "./DataTable/CellWithTooltip";
import { TooltipProvider } from "@/components/ui/tooltip";

// Re-export for backward compatibility
export type { FilterType, NumberFilterValue, DataTableProps } from "./DataTable/types";
export { createFilterString, createFilterNumber, createFilterEnum } from "./DataTable/filters";
export { StringFilter, NumberFilter, EnumFilter } from "./DataTable/filters";
export { createSortableHeader } from "./DataTable/headers";

export function DataTable<T>({
  columns,
  columnLabels,
  columnTypes,
  enumValues,
  apiEndpoint,
  storagePrefix,
  title,
  icon,
  globalFilterFn,
  emptyMessage = "No items available",
  emptyFilteredMessage = "No items found matching your filters",
  itemName = "items",
  dataParser,
  initialColumnVisibility,
  initialColumnOrder,
}: DataTableProps<T>) {
  const COLUMN_VISIBILITY_STORAGE_KEY = `${storagePrefix}-table-column-visibility`;
  const COLUMN_ORDER_STORAGE_KEY = `${storagePrefix}-table-column-order`;
  const COLUMN_SIZING_STORAGE_KEY = `${storagePrefix}-table-column-sizes`;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string>("latest");
  const [availableVersions, setAvailableVersions] = useState<string[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  // Load column visibility from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (saved) {
      try {
        setColumnVisibility(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load column visibility preferences", e);
      }
    } else if (initialColumnVisibility) {
      // Use initial visibility if no saved state exists
      setColumnVisibility(initialColumnVisibility);
    }
  }, [COLUMN_VISIBILITY_STORAGE_KEY, initialColumnVisibility]);

  // Save column visibility to localStorage when it changes
  useEffect(() => {
    if (Object.keys(columnVisibility).length > 0) {
      localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
    }
  }, [columnVisibility, COLUMN_VISIBILITY_STORAGE_KEY]);

  // Load column order from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
    if (saved) {
      try {
        const savedOrder = JSON.parse(saved);
        if (Array.isArray(savedOrder) && savedOrder.length > 0) {
          setColumnOrder(savedOrder);
        }
      } catch (e) {
        console.error("Failed to load column order preferences", e);
      }
    }
  }, [COLUMN_ORDER_STORAGE_KEY]);

  // Save column order to localStorage when it changes
  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(columnOrder));
    }
  }, [columnOrder, COLUMN_ORDER_STORAGE_KEY]);

  // Load column sizing from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_SIZING_STORAGE_KEY);
    if (saved) {
      try {
        setColumnSizing(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load column sizing preferences", e);
      }
    }
  }, [COLUMN_SIZING_STORAGE_KEY]);

  // Save column sizing to localStorage when it changes
  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      localStorage.setItem(COLUMN_SIZING_STORAGE_KEY, JSON.stringify(columnSizing));
    }
  }, [columnSizing, COLUMN_SIZING_STORAGE_KEY]);

  // Fetch available versions
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        const res = await fetch(`${apiEndpoint}/versions`);
        if (res.ok) {
          const versions = await res.json();
          const versionList = versions.map((v: { version: string }) => v.version);
          setAvailableVersions(versionList);
        }
      } catch (err) {
        console.error("Failed to fetch versions:", err);
      }
    };
    fetchVersions();
  }, [apiEndpoint]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${apiEndpoint}/${selectedVersion}`);
        if (!res.ok) {
          throw new Error(`Failed to fetch data: ${res.statusText}`);
        }
        const responseData = await res.json();
        // Parse data using provided parser or default logic
        let items: T[];
        if (dataParser) {
          items = dataParser(responseData);
        } else {
          // Default: try array first, then object with items property
          items = Array.isArray(responseData) ? responseData : (responseData.items || (() => {
            const firstKey = Object.keys(responseData)[0];
            return firstKey ? responseData[firstKey] : [];
          })() || []);
        }
        setData(items);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiEndpoint, selectedVersion, dataParser]);

  // Initialize column order if not loaded from localStorage
  useEffect(() => {
    if (columnOrder.length === 0 && columns.length > 0) {
      const saved = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
      if (!saved) {
        if (initialColumnOrder && initialColumnOrder.length > 0) {
          // Use initial column order if provided
          setColumnOrder(initialColumnOrder);
        } else {
          // Otherwise use default order (all columns in definition order)
          const allColumnIds = columns.map(col => {
            if ('accessorKey' in col && col.accessorKey) {
              return col.accessorKey as string;
            }
            if ('id' in col && col.id) {
              return col.id;
            }
            return null;
          }).filter((id): id is string => id !== null);
          
          if (allColumnIds.length > 0) {
            setColumnOrder(allColumnIds);
          }
        }
      }
    }
  }, [columns, columnOrder.length, COLUMN_ORDER_STORAGE_KEY, initialColumnOrder]);

  // Default global filter function
  const defaultGlobalFilterFn: FilterFn<T> = (row, columnId, filterValue: string) => {
    const search = filterValue.toLowerCase();
    // Try to search in common fields
    const rowData = row.original as any;
    if (rowData.name && typeof rowData.name === 'string') {
      return rowData.name.toLowerCase().includes(search);
    }
    if (rowData.descriptorName && typeof rowData.descriptorName === 'string') {
      return rowData.descriptorName.toLowerCase().includes(search);
    }
    return false;
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 50,
      size: 100,
      maxSize: 1000,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    globalFilterFn: globalFilterFn || defaultGlobalFilterFn,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnOrder: columnOrder.length > 0 ? columnOrder : undefined,
      columnSizing,
    },
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  const activeFilterCount = columnFilters.length;

  // Reset to default column order and visibility
  const handleResetColumns = () => {
    const defaultOrder = columns.map(col => {
      if ('accessorKey' in col && col.accessorKey) {
        return col.accessorKey as string;
      }
      if ('id' in col && col.id) {
        return col.id;
      }
      return null;
    }).filter((id): id is string => id !== null);
    
    setColumnOrder(defaultOrder);
    setColumnVisibility({});
    setColumnSizing({});
    localStorage.removeItem(COLUMN_ORDER_STORAGE_KEY);
    localStorage.removeItem(COLUMN_VISIBILITY_STORAGE_KEY);
    localStorage.removeItem(COLUMN_SIZING_STORAGE_KEY);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={() => window.location.reload()} variant="outline">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-4 flex flex-col h-full">
      <Card className="flex flex-col h-full">
        <CardHeader className="flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <CardTitle>{title} Table</CardTitle>
            <div className="flex gap-2 w-full sm:w-auto flex-wrap min-w-0">
              <Select value={selectedVersion} onValueChange={setSelectedVersion}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Select version" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Latest</SelectItem>
                  {availableVersions.map((version) => (
                    <SelectItem key={version} value={version}>
                      {version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FilterDialog
                table={table}
                columnLabels={columnLabels}
                columnTypes={columnTypes}
                enumValues={enumValues}
                activeFilterCount={activeFilterCount}
                onClearAll={() => setColumnFilters([])}
                columnOrder={columnOrder.length > 0 ? columnOrder : undefined}
              />
              <ColumnConfigDialog
                table={table}
                columnLabels={columnLabels}
                columnOrder={columnOrder}
                setColumnOrder={setColumnOrder}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
                columns={columns}
                onReset={handleResetColumns}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col overflow-hidden min-h-0">
          <div className="rounded-md border overflow-y-auto flex-1">
            <TooltipProvider>
              <table className="w-full caption-bottom text-sm" style={{ tableLayout: 'fixed' }}>
                <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => {
                  // Calculate left offset for sticky columns
                  let stickyLeftOffset = 0;
                  return (
                    <TableRow key={headerGroup.id} className="sticky top-0 z-30 bg-background">
                      {headerGroup.headers.map((header) => {
                        const isSticky = (header.column.columnDef.meta as any)?.sticky === true;
                        const stickyStyle = isSticky ? {
                          position: 'sticky' as const,
                          left: `${stickyLeftOffset}px`,
                          zIndex: 40,
                          backgroundColor: 'hsl(var(--background))',
                          boxShadow: stickyLeftOffset === 0 ? '2px 0 4px rgba(0,0,0,0.1)' : 'none',
                          borderRight: '1px solid hsl(var(--border))',
                        } : {};
                        
                        if (isSticky) {
                          stickyLeftOffset += header.getSize();
                        }
                        
                        return (
                          <TableHead 
                            key={header.id}
                            data-sticky={isSticky ? 'true' : undefined}
                            style={{ 
                              width: `${header.getSize()}px`,
                              position: isSticky ? 'sticky' : 'relative',
                              overflow: 'hidden',
                              paddingLeft: '4px',
                              paddingRight: '4px',
                              ...stickyStyle,
                            }}
                            className={isSticky ? "overflow-hidden sticky bg-background" : "relative overflow-hidden"}
                          >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        <div
                          {...(header.column.getCanResize() ? {
                            onMouseDown: header.getResizeHandler(),
                            onTouchStart: header.getResizeHandler(),
                          } : {})}
                          onMouseEnter={(e) => {
                            if (!header.column.getIsResizing()) {
                              (e.currentTarget as HTMLElement).style.backgroundColor = 'hsl(var(--primary) / 0.7)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!header.column.getIsResizing()) {
                              (e.currentTarget as HTMLElement).style.backgroundColor = 'hsl(var(--muted-foreground) / 0.4)';
                            }
                          }}
                          style={{
                            position: 'absolute',
                            right: '-2px',
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            zIndex: 20,
                            backgroundColor: header.column.getIsResizing() 
                              ? 'hsl(var(--primary))' 
                              : 'hsl(var(--muted-foreground) / 0.4)',
                          }}
                          title="Drag to resize column"
                        />
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => {
                    // Calculate left offset for sticky columns
                    let stickyLeftOffset = 0;
                    return (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => {
                          const isSticky = (cell.column.columnDef.meta as any)?.sticky === true;
                          const stickyStyle = isSticky ? {
                            position: 'sticky' as const,
                            left: `${stickyLeftOffset}px`,
                            zIndex: 10,
                            backgroundColor: 'var(--background)',
                            boxShadow: stickyLeftOffset === 0 ? '2px 0 4px rgba(0,0,0,0.1)' : 'none',
                            borderRight: '1px solid hsl(var(--border))',
                          } : {};
                          
                          if (isSticky) {
                            stickyLeftOffset += cell.column.getSize();
                          }
                          
                          const cellContent = flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          );
                          return (
                            <TableCell 
                              key={cell.id}
                              data-sticky={isSticky ? 'true' : undefined}
                              style={{ 
                                width: `${cell.column.getSize()}px`,
                                overflow: 'hidden',
                                ...stickyStyle,
                              }}
                              className={isSticky ? "overflow-hidden sticky bg-background" : "overflow-hidden"}
                            >
                            <CellWithTooltip content={cell.getValue() as string}>
                              {cellContent}
                            </CellWithTooltip>
                          </TableCell>
                        );
                      })}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {globalFilter || activeFilterCount > 0
                        ? emptyFilteredMessage
                        : emptyMessage}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              </table>
            </TooltipProvider>
          </div>

          {/* Pagination Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-4 flex-shrink-0 flex-wrap">
            {/* Left side: Results info and page size */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 flex-wrap">
              <p className="text-sm text-muted-foreground">
                <span className="hidden sm:inline">
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )}{" "}
                  of {table.getFilteredRowModel().rows.length} {itemName}
                  {(globalFilter || activeFilterCount > 0) && " (filtered)"}
                </span>
                <span className="sm:hidden">
                  {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-{Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )} of {table.getFilteredRowModel().rows.length}
                  {(globalFilter || activeFilterCount > 0) && " (filtered)"}
                </span>
              </p>
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground hidden sm:inline">Rows per page:</p>
                <p className="text-sm text-muted-foreground sm:hidden">Per page:</p>
                <Select
                  value={`${table.getState().pagination.pageSize}`}
                  onValueChange={(value) => {
                    table.setPageSize(Number(value));
                  }}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Right side: Navigation controls */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="flex-shrink-0"
              >
                <ChevronLeft className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Previous</span>
              </Button>
              <div className="flex items-center gap-1 px-2">
                <span className="text-sm text-muted-foreground hidden md:inline">Page</span>
                <strong className="text-sm">
                  {table.getState().pagination.pageIndex + 1}<span className="hidden sm:inline"> of {table.getPageCount()}</span>
                </strong>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="flex-shrink-0"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="h-4 w-4 sm:ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
